import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { portfoliosService, type Portfolio, type PortfolioDetails } from '../../../services/projectManagement/portfoliosService';
import { projectsService, type Project } from '../../../services/projectManagement/projectsService';

const PortfoliosTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();

  const [loading, setLoading] = useState(true);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState<PortfolioDetails | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');

  const [showAddProject, setShowAddProject] = useState(false);
  const [addProjectId, setAddProjectId] = useState('');

  const fetchPortfolios = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const { data, error } = await portfoliosService.list(activeOrgId);
      if (error) {
        toast.error(String(error || 'Failed to load portfolios'));
        setPortfolios([]);
        return;
      }
      setPortfolios(data || []);
      if (!selectedId && data?.[0]?.id) {
        setSelectedId(data[0].id);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, selectedId]);

  const fetchProjects = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const { data, error } = await projectsService.list({ organization_id: activeOrgId });
      if (error) return;
      setProjects(data || []);
    } catch {
      setProjects([]);
    }
  }, [activeOrgId]);

  const fetchDetails = useCallback(async () => {
    if (!activeOrgId || !selectedId) {
      setDetails(null);
      return;
    }
    setDetailsLoading(true);
    try {
      const { data, error } = await portfoliosService.getById(selectedId, activeOrgId);
      if (error) {
        toast.error(String(error || 'Failed to load portfolio'));
        setDetails(null);
        return;
      }
      setDetails(data);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load portfolio');
      setDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  }, [activeOrgId, selectedId]);

  useEffect(() => {
    void fetchPortfolios();
    void fetchProjects();
  }, [fetchPortfolios, fetchProjects]);

  useEffect(() => {
    void fetchDetails();
  }, [fetchDetails]);

  const selected = useMemo(() => portfolios.find((p) => p.id === selectedId) || null, [portfolios, selectedId]);

  const availableProjects = useMemo(() => {
    const linked = new Set((details?.projects || []).map((p) => String(p.id)));
    return (projects || []).filter((p) => !linked.has(String(p.id)));
  }, [projects, details]);

  const createPortfolio = async () => {
    if (!activeOrgId) return;
    const name = createName.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }

    try {
      const { data, error } = await portfoliosService.create({
        organization_id: activeOrgId,
        name,
        description: createDescription.trim() || null,
      });

      if (error) {
        toast.error(String(error || 'Failed to create portfolio'));
        return;
      }

      setShowCreate(false);
      setCreateName('');
      setCreateDescription('');

      if (data?.id) setSelectedId(data.id);
      void fetchPortfolios();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create portfolio');
    }
  };

  const addProject = async () => {
    if (!activeOrgId || !selectedId) return;
    const projectId = addProjectId.trim();
    if (!projectId) {
      toast.error('Select a project');
      return;
    }

    try {
      const { error } = await portfoliosService.addProject(selectedId, {
        organization_id: activeOrgId,
        project_id: projectId,
      });

      if (error) {
        toast.error(String(error || 'Failed to add project'));
        return;
      }

      setShowAddProject(false);
      setAddProjectId('');
      void fetchDetails();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add project');
    }
  };

  const removeProject = async (projectId: string) => {
    if (!activeOrgId || !selectedId) return;
    try {
      const { error } = await portfoliosService.removeProject(selectedId, {
        organization_id: activeOrgId,
        project_id: projectId,
      });
      if (error) {
        toast.error(String(error || 'Failed to remove project'));
        return;
      }
      void fetchDetails();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to remove project');
    }
  };

  if (!activeOrgId) {
    return <div className="rounded-xl bg-white p-6">Select an organization to view portfolios.</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">Portfolios</div>
            <div className="text-xs text-gray-500">Roll up multiple projects into one view</div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90"
          >
            New
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : portfolios.length === 0 ? (
            <div className="text-sm text-gray-500">No portfolios yet.</div>
          ) : (
            portfolios.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                  p.id === selectedId ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">{p.name}</div>
                <div className="text-xs text-gray-500">{p.status}</div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          {detailsLoading ? (
            <div className="text-sm text-gray-500">Loading portfolio...</div>
          ) : !selected ? (
            <div className="text-sm text-gray-500">Select a portfolio.</div>
          ) : !details ? (
            <div className="text-sm text-gray-500">Portfolio details unavailable.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-gray-900">{details.portfolio.name}</div>
                  {details.portfolio.description ? (
                    <div className="mt-1 text-sm text-gray-600">{details.portfolio.description}</div>
                  ) : null}
                </div>
                <button
                  onClick={() => setShowAddProject(true)}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Add project
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Completion</div>
                  <div className="text-sm font-semibold text-gray-900">{details.rollup.completion_percentage}%</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Projects</div>
                  <div className="text-sm font-semibold text-gray-900">{details.rollup.total_projects}</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">At risk</div>
                  <div className="text-sm font-semibold text-gray-900">{details.rollup.projects_at_risk}</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Blocked</div>
                  <div className="text-sm font-semibold text-gray-900">{details.rollup.blocked_tasks}</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Overdue</div>
                  <div className="text-sm font-semibold text-gray-900">{details.rollup.overdue_tasks}</div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200">
                <div className="border-b border-gray-200 px-4 py-3">
                  <div className="text-sm font-semibold text-gray-900">Projects in portfolio</div>
                </div>
                <div className="divide-y">
                  {(details.projects || []).length === 0 ? (
                    <div className="px-4 py-4 text-sm text-gray-500">No projects added yet.</div>
                  ) : (
                    details.projects.map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{p.name}</div>
                          <div className="text-xs text-gray-500">
                            {p.status} · {p.completion_percentage}% · {p.overdue_tasks} overdue · {p.blocked_tasks} blocked
                          </div>
                        </div>
                        <button
                          onClick={() => removeProject(String(p.id))}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">New portfolio</div>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700">Name</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700">Description</label>
                <textarea
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  rows={4}
                />
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createPortfolio}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddProject && details && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">Add project</div>
              <button onClick={() => setShowAddProject(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-700">Project</label>
              <select
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={addProjectId}
                onChange={(e) => setAddProjectId(e.target.value)}
              >
                <option value="">Select a project</option>
                {availableProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setShowAddProject(false)}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addProject}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfoliosTab;
